import { GetServerSideProps } from 'next';
import { useEffect, useState } from 'react';
import Head from 'next/head';
import { useTranslation } from 'react-i18next';
import { getEvent } from '../../lib/parseClient';
import { Event } from '../../lib/types';
import EventHeader from '../../components/EventHeader';
import EventCard from '../../components/EventCard';
import EventContainer from '../../components/EventContainer';
import EventDetails from '../../components/EventDetails';
import SocialMetaTags from '../../components/SocialMetaTags';
import ShareEvent from '../../components/ShareEvent';
import PublicRsvpModal from '../../components/PublicRsvpModal';
import RsvpClosedModal from '../../components/RsvpClosedModal';
import MapModal from '../../components/MapModal';
import EventFooter from '../../components/EventFooter';
import FullscreenImageModal from '../../components/FullscreenImageModal';
import { getEventImage } from '../../lib/utils';
import LanguageSelector from '../../components/LanguageSelector';
import PostHogFeatures from '../../components/PostHogFeatures';

interface PublicEventPageProps {
  event: Event;
}

export default function PublicEventPage({ event }: PublicEventPageProps) {
  const { t } = useTranslation();
  const [isRsvpOpen, setIsRsvpOpen] = useState(false);
  const [isRsvpClosedOpen, setIsRsvpClosedOpen] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [isFullscreenImageOpen, setIsFullscreenImageOpen] = useState(false);
  const [hideAds, setHideAds] = useState(false);


  // RevenueCat entitlement check on page load
  useEffect(() => {
    async function checkEntitlements() {
      try {
        const userId = event?.user_objectId?.id || event?.user_objectId?.objectId;
        if (!userId) {
          setHideAds(true); // Hide ads if we can't determine the user
          return;
        }
        const resp = await fetch(`/api/revenuecat/entitlements?userId=${encodeURIComponent(userId)}`, { cache: 'no-store' });
        if (!resp.ok) {
          console.error('RevenueCat API error:', resp.status);
          setHideAds(true); // Hide ads on API error
          return;
        }
        const data = await resp.json();
        // Only show ads if we're certain the user doesn't have an entitlement
        setHideAds(data.hasActiveEntitlement !== false);
      } catch (e) {
        console.error('RevenueCat check failed:', e);
        setHideAds(true); // Hide ads on any error
      }
    }
    checkEntitlements();
  }, [event?.user_objectId]);

  const handleRsvpClick = () => {
    // For public events, if RSVPs are disabled, always show closed modal
    if (event.rsvp_enabled === false) {
      setIsRsvpClosedOpen(true);
      return;
    }
    
    // Otherwise, open the normal RSVP modal
    setIsRsvpOpen(true);
  };

  return (
    <>
      <SocialMetaTags
        title={event.event_name}
        description={`Join us at ${event.event_name} - Event RSVPs by Invitd`}
        event={event}
        url={`/public/${event.id}`}
        type="article"
      />
      
      <PostHogFeatures 
        eventId={event.id}
        userRole="public"
      />

      <main className="min-h-screen bg-gray-50">
        <EventHeader event={event} onImageClick={() => setIsFullscreenImageOpen(true)} />
        
        {/* Main Event Card */}
        <EventCard 
          event={event}
          onOpenMap={() => setIsMapOpen(true)} 
          onRsvpClick={handleRsvpClick}
        />
        
        <EventContainer>
          <EventDetails event={event} onOpenMap={() => setIsMapOpen(true)} hideAd={hideAds} />
          <ShareEvent event={event} />
          <LanguageSelector />
        </EventContainer>

        <EventFooter 
          onRsvpClick={handleRsvpClick}
          isRsvpEnabled={true}
          hasResponded={false}
          isModalOpen={isRsvpOpen || isRsvpClosedOpen}
        />

        <PublicRsvpModal
          isOpen={isRsvpOpen}
          onClose={() => setIsRsvpOpen(false)}
          event={event}
          onRsvpComplete={() => {
            // Refresh the page to show updated RSVP status
            window.location.reload();
          }}
        />

        <RsvpClosedModal
          isOpen={isRsvpClosedOpen}
          onClose={() => setIsRsvpClosedOpen(false)}
        />

        <MapModal
          isOpen={isMapOpen}
          onClose={() => setIsMapOpen(false)}
          event={event}
        />

        <FullscreenImageModal
          isOpen={isFullscreenImageOpen}
          onClose={() => setIsFullscreenImageOpen(false)}
          event={event}
        />
      </main>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { id } = context.query;
  
  if (!id || typeof id !== 'string') {
    return {
      redirect: {
        destination: `/invalid-link?from=/public/${context.query.id || ''}`,
        permanent: false,
      },
    };
  }

  // Skip processing for common static file requests that might slip through
  if (id.includes('.png') || id.includes('.jpg') || id.includes('.ico') || 
      id.includes('.svg') || id.includes('.gif') || id.includes('.webp')) {
    return {
      notFound: true,
    };
  }

  try {
    const event = await getEvent(id);
    
    if (!event || !event.is_public) {
      return {
      redirect: {
        destination: `/invalid-link?from=/public/${context.query.id || ''}`,
        permanent: false,
      },
    };
    }

    // If event status is inactive, redirect to event-over page
    if (event.event_status === 'inactive') {
      return {
        redirect: {
          destination: `/event-over?from=/public/${id}&name=${encodeURIComponent(event.event_name || '')}`,
          permanent: false,
        },
      };
    }

    return {
      props: {
        event: event,
      },
    };
  } catch (error) {
    console.error(`Error fetching event for ID "${id}":`, error);
    console.error(`Event ID type: ${typeof id}, User-Agent: ${context.req.headers['user-agent'] || 'N/A'}`);
    return {
      redirect: {
        destination: `/invalid-link?from=/public/${context.query.id || ''}`,
        permanent: false,
      },
    };
  }
};